import { useCallback, useContext, useState } from 'react';
import type { MouseEventHandler, ReactNode } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useFloating } from '@floating-ui/react';
import { ProjectClipsSortContext } from '../../contexts/project-clips/Context';
import { useIsProjectClipColumnSorted } from '../../hooks/useIsProjectClipColumnSorted';
import { useZIndexMiddleware } from '../presentation/popper-modifiers';
import { Portal } from '../Portal/index.js';
import { SortableHeader } from './SortableHeader';
import { FilterIconButton } from './FilterIconButton';

type Props = {
  columnId: string;
  children: ReactNode;
  /** Render function receiving a close callback. If provided, shows a filter icon. */
  filterMenu?: (onClose: () => void) => ReactNode;
  isFilterActive?: boolean;
  sortable?: boolean;
};

export const FilterableHeader = ({
  columnId,
  children,
  filterMenu,
  isFilterActive = false,
  sortable = true,
}: Props) => {
  const { onSortClick } = useContext(ProjectClipsSortContext);
  const sorted = useIsProjectClipColumnSorted(columnId);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterEl, setFilterEl] = useState<HTMLElement | null>(null);

  const handleHeaderClick: MouseEventHandler = useCallback(
    (e) => {
      if (filterEl?.contains(e.target as Node)) return;
      if (sortable) onSortClick(columnId);
    },
    [filterEl, sortable, onSortClick, columnId]
  );

  const zIndexMiddleware = useZIndexMiddleware(8);
  const { refs, floatingStyles } = useFloating({
    elements: { reference: filterEl ?? undefined },
    open: filterMenuOpen,
    onOpenChange: setFilterMenuOpen,
    middleware: [zIndexMiddleware],
    placement: 'bottom-start',
  });

  const handleFilterClick = useCallback(() => setFilterMenuOpen(true), []);
  const handleClose = useCallback(() => setFilterMenuOpen(false), []);

  return (
    <>
      <SortableHeader onClick={handleHeaderClick} sortable={sortable} sorted={sorted}>
        {filterMenu != null && (
          <div
            ref={(node) => {
              setFilterEl(node);
              refs.setReference(node);
            }}
            onClick={handleFilterClick}
          >
            <FilterIconButton isActive={isFilterActive} />
          </div>
        )}
        {children}
      </SortableHeader>
      {filterMenu != null && filterMenuOpen && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose} touchEvent="onTouchStart">
            <div ref={refs.setFloating} style={floatingStyles}>
              {filterMenu(handleClose)}
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  );
};

FilterableHeader.displayName = 'ProjectClips.FilterableHeader';
